import { Dispatch } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import i18n from 'i18n';
import { apiClient } from 'services/restService';
import { openSnackbar } from 'features/app/appSlice';
import {
  createGetHeaderData,
  createHeaderData,
  createPutHeaderData,
  createQueryParams,
  handlePreconditionFailed
} from 'utils/restUtils';
import {
  setActivityLogs,
  setCalendarActivityLog,
  setCodebooks,
  setDispatcherContextActions,
  setIntoTimeSeriesForReservedCapacityGuardingBatch,
  setIntoTimeSeriesForHxBatch,
  setIntoTimeSeriesForOnlineDispatchingBatch,
  setIntoTimeSeriesForPlanningBatch,
  setMetricSpecs,
  setOperationCalendarList,
  setOperationEventDetail,
  setOperationEventList,
  setSseToken
} from 'features/dispatcher/dispatcherSlice';
import { HttpHeaders } from 'models';
import { AppThunk } from 'models/store';
import {
  BESortDirection,
  CreateOperationEventForContractInterface,
  CreateOperationEventInterface,
  FetchActivityLogsParams,
  GetMarketHoursResponse,
  GetTimeSeriesListParams,
  MonitoredResourceType,
  MonitoredResourceTypeLabel,
  SetIntoTimeSeriesPayload,
  TimeSeriesInterface,
  TimeSeriesQueryInterface
} from 'features/dispatcher/dispatcherTypes';
import {
  GetContextActionsRequestBody,
  ResourceContextAction
} from 'models/contextActions';
import { DispatcherEtagEntityList } from 'models/etag';
import { UpdateOperationEventRequestBody } from 'features/eai/eaiTypes';
import {
  CountryCode,
  TIMESERIES_LIST_BATCH_SIZE,
  TimeSeriesType
} from 'utils/constants';
import { logError } from 'services/Logging';

const dispatcherPrefix = '/dispatcher';
const operationCalendarPrefix = '/dispatcher/operation-calendar';

// ==============================|| DISPATCHER THUNKS ||============================== //

export const getDispatcherCodebooks =
  (): AppThunk => (dispatch: Dispatch<ReturnType<typeof setCodebooks>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      false,
      'application/hal+json'
    );

    apiClient
      .get(`${dispatcherPrefix}/list-of-values`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setCodebooks(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchAndSetDispatcherContextActions =
  (requestBody: GetContextActionsRequestBody): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDispatcherContextActions>>) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');

    apiClient
      .post(`${dispatcherPrefix}/rule-engine/get-context-action`, requestBody, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(
          setDispatcherContextActions(
            response.data._embedded
              .getContextActionDtoList as ResourceContextAction[]
          )
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchAndSetSseToken =
  (): AppThunk => (dispatch: Dispatch<ReturnType<typeof setSseToken>>) => {
    const headers: HttpHeaders = createGetHeaderData();

    apiClient
      .get(`${dispatcherPrefix}/server-sent/token`, { headers })
      .then((response: AxiosResponse) => {
        const { data } = response;

        dispatch(setSseToken(data.token));
      })
      .catch((error) => logError(error));
  };

export const createOperationEvent =
  (
    id: string,
    requestBody:
      | CreateOperationEventInterface
      | CreateOperationEventForContractInterface,
    callback?: () => any
  ) =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createPutHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .put(
        `${operationCalendarPrefix}/${id}/operation-event`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.dispatcher.messages.eventCreated'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.dispatcher.messages.errorCreatingEvent'),
            variant: 'alert',
            alert: {
              color: 'error'
            },
            close: false
          })
        );
        logError(error);
      });
  };

export const getOperationCalendarList =
  (): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setOperationCalendarList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`${operationCalendarPrefix}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(setOperationCalendarList(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getOperationEventList =
  (
    startDate: string,
    endDate: string,
    balancingGroups: string[],
    deliveryPoints: string[],
    devices: string[]
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setOperationEventList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );
    const queryParams = createQueryParams({
      balancingGroups: balancingGroups, // List<Long>
      deliveryPoints: deliveryPoints, // List<String>
      devices: devices, // List<String>
      startDateTime: startDate,
      endDateTime: endDate,
      includeHistory: false
    });

    apiClient
      .get(`${operationCalendarPrefix}/operation-event${queryParams}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(setOperationEventList(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getOperationEventDetail =
  (operationEventId: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setOperationEventDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(
        `${operationCalendarPrefix}/operation-event/detail/${operationEventId}`,
        {
          headers,
          data: { etagEntity: DispatcherEtagEntityList.OPERATION_EVENT }
        }
      )
      .then((response: AxiosResponse) => {
        dispatch(setOperationEventDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateOperationEvent =
  (
    calendarId: string,
    operationEventId: string,
    requestBody: UpdateOperationEventRequestBody,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof updateOperationEvent>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .patch(
        `${operationCalendarPrefix}/${calendarId}/operation-event/${operationEventId}`,
        { ...requestBody },
        { headers }
      )
      .then((_: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.dispatcher.messages.updateOperationCalendarEvent'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(getOperationEventDetail(operationEventId));
          },
          () =>
            dispatch(
              updateOperationEvent(
                calendarId,
                operationEventId,
                requestBody,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const invalidateOperationEvent =
  (
    calendarId: string,
    operationEventId: string,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof updateOperationEvent>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .patch(
        `${operationCalendarPrefix}/${calendarId}/operation-event/${operationEventId}/invalidate`,
        {},
        { headers }
      )
      .then((_: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.dispatcher.messages.invalidateOperationEvent'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(getOperationEventDetail(operationEventId));
          },
          () =>
            dispatch(
              invalidateOperationEvent(
                calendarId,
                operationEventId,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const deleteOperationEvent =
  (
    calendarId: string,
    operationEventId: string,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof invalidateOperationEvent>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .delete(
        `${operationCalendarPrefix}/${calendarId}/operation-event/${operationEventId}`,
        { headers }
      )
      .then((_: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.dispatcher.messages.deleteOperationCalendarEvent'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(getOperationEventDetail(operationEventId));
          },
          () =>
            dispatch(
              invalidateOperationEvent(
                calendarId,
                operationEventId,
                undefined,
                callback
              )
            )
        );
        logError(error);
      });
  };

export const getActivityEvents =
  (
    code = '',
    operationCalendarIds: string[] = [],
    sortDirection: BESortDirection = BESortDirection[BESortDirection.DESC]
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setCalendarActivityLog>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    const queryParams = createQueryParams({
      code: code,
      operationCalendarIds: operationCalendarIds, // List<String>
      sortDirection: sortDirection
    });

    apiClient
      .get(`${operationCalendarPrefix}/activity-log${queryParams}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(setCalendarActivityLog(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getAllTimeSeriesLists = async (
  timeSeriesQueries: TimeSeriesQueryInterface[]
): Promise<SetIntoTimeSeriesPayload[]> => {
  const headers: HttpHeaders = createHeaderData(true, '*/*');

  const promises = [];

  //split request body to chunks because of performance reasons - BE has trouble handling requests with length > 10
  for (
    let i = 0;
    i < timeSeriesQueries.length;
    i += TIMESERIES_LIST_BATCH_SIZE
  ) {
    const batch = timeSeriesQueries.slice(i, i + TIMESERIES_LIST_BATCH_SIZE);
    const batchRequest = apiClient.post(
      `${dispatcherPrefix}/time-series/list`,
      batch,
      {
        headers,
        hideError404: true
      }
    );

    promises.push(batchRequest);
  }

  const dataToReturn: SetIntoTimeSeriesPayload[] = [];
  //handle responses once all requests are finished
  try {
    const responses = await Promise.all(promises);

    responses.forEach((response) => {
      if (!response.data._embedded) {
        return;
      }

      const data = response.data._embedded.timeSeriesDtoList;

      const processedDataOfBatch = data.map((item: TimeSeriesInterface) => {
        const resourceType = item.monitoredResource
          .type as MonitoredResourceType;

        let resourceLabel: MonitoredResourceTypeLabel | undefined;

        if (
          item.monitoredResource.labels &&
          item.monitoredResource.labels.length > 0
        ) {
          resourceLabel = item.monitoredResource.labels[0]
            .key as MonitoredResourceTypeLabel;
        }

        return { data: item, resource: { resourceType, resourceLabel } };
      });

      dataToReturn.push(...processedDataOfBatch);
    });
  } catch (error) {
    logError(error);
  }

  return dataToReturn;
};

export const getTimeSeriesList =
  ({
    ignoreMetricLabels,
    requestBody,
    timeSeriesType,
    overwriteOldDataPoints,
    callback
  }: GetTimeSeriesListParams): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<
        | typeof setIntoTimeSeriesForPlanningBatch
        | typeof setIntoTimeSeriesForOnlineDispatchingBatch
        | typeof setIntoTimeSeriesForHxBatch
        | typeof setIntoTimeSeriesForReservedCapacityGuardingBatch
      >
    >
  ) => {
    getAllTimeSeriesLists(requestBody).then((responses) => {
      if (timeSeriesType === TimeSeriesType.ONLINE_DISPATCHING) {
        dispatch(
          setIntoTimeSeriesForOnlineDispatchingBatch({
            setIntoTimeSeriesPayload: responses,
            overwriteOldDataPoints
          })
        );
      }
      if (timeSeriesType === TimeSeriesType.PLANNING) {
        dispatch(
          setIntoTimeSeriesForPlanningBatch({
            setIntoTimeSeriesPayload: responses,
            overwriteOldDataPoints
          })
        );
      }
      if (timeSeriesType === TimeSeriesType.HX) {
        dispatch(
          setIntoTimeSeriesForHxBatch({
            setIntoTimeSeriesPayload: responses,
            overwriteOldDataPoints
          })
        );
      }
      if (timeSeriesType === TimeSeriesType.RESERVED_CAPACITY_GUARDING) {
        dispatch(
          setIntoTimeSeriesForReservedCapacityGuardingBatch({
            ignoreMetricLabels,
            setIntoTimeSeriesPayload: responses,
            overwriteOldDataPoints
          })
        );
      }

      callback?.(responses);
    });
  };

export const getMetricSpecs =
  (): AppThunk => (dispatch: Dispatch<ReturnType<typeof setMetricSpecs>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      false,
      'application/hal+json'
    );

    apiClient
      .get(`/dispatcher/metric-spec`, { headers })
      .then((response) => {
        dispatch(setMetricSpecs(response.data));
      })
      .catch((error) => logError(error));
  };

export const fetchMarketTimeRules = async (
  marketDay: string
): Promise<GetMarketHoursResponse | undefined> => {
  const headers: HttpHeaders = createGetHeaderData(
    false,
    'application/hal+json'
  );

  const countryCode = CountryCode.SVK;
  const queryParams = createQueryParams({
    marketDay,
    countryCode
  });

  const response = await apiClient.get(
    `${dispatcherPrefix}/list-of-values/market-time-rules${queryParams}`,
    { headers }
  );
  if (response?.data) {
    return response.data as GetMarketHoursResponse;
  }
  return undefined;
};

export const fetchAndSetActivityLogs =
  (queryParams: FetchActivityLogsParams): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setActivityLogs>>) => {
    const headers = createGetHeaderData(true, 'application/hal+json');
    const queryParamsString = createQueryParams(queryParams);

    apiClient
      .get(`${dispatcherPrefix}/activity-event${queryParamsString}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(
          setActivityLogs(response.data._embedded?.activityEventDtoList || [])
        );
      })
      .catch((error) => logError(error));
  };
